import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Divider,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import PageHeader from '@/components/common/PageHeader';
import DataTable, { Column } from '@/components/common/DataTable';
import { usersApi, Reseller, Customer } from '@/api/users-api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reseller-tabpanel-${index}`}
      aria-labelledby={`reseller-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function ResellerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();

  const [reseller, setReseller] = useState<Reseller | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contactDetails: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchResellerData = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);

      try {
        const resellerData = await usersApi.getReseller(id);
        setReseller(resellerData);
        setFormData({
          name: resellerData.name,
          contactDetails: resellerData.contactDetails,
        });

        const customersData = await usersApi.getCustomers(id);
        setCustomers(customersData);
      } catch (err) {
        console.error('Error fetching reseller data:', err);
        setError('Failed to load reseller data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && user && id) {
      fetchResellerData();
    }
  }, [authLoading, user, id]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditReseller = async () => {
    if (!id) return;

    try {
      await usersApi.updateReseller(id, {
        name: formData.name,
        contactDetails: formData.contactDetails,
      });
      setOpenEditDialog(false);
      setSuccessMessage('Reseller updated successfully');

      const updatedReseller = await usersApi.getReseller(id);
      setReseller(updatedReseller);
    } catch (err) {
      console.error('Error updating reseller:', err);
      setError('Failed to update reseller. Please try again.');
    }
  };

  const handleAddCustomer = () => {
    navigate(`/customers/new?reseller=${id}`);
  };

  const customerColumns: Column<Customer>[] = [
    {
      id: 'name',
      label: 'Customer Name',
      minWidth: 150,
    },
    {
      id: 'contactDetails',
      label: 'Contact Details',
      minWidth: 200,
    },
    {
      id: 'createdAt',
      label: 'Created On',
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user && !authLoading) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">You must be logged in to view this page.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/resellers')}
          sx={{ mt: 2 }}
        >
          Back to Resellers
        </Button>
      </Box>
    );
  }

  if (!reseller) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="warning">Reseller not found.</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/resellers')}
          sx={{ mt: 2 }}
        >
          Back to Resellers
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageHeader
        title={`Reseller: ${reseller.name}`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Resellers', href: '/resellers' },
          { label: reseller.name },
        ]}
        action={{
          label: 'Edit Reseller',
          onClick: () => setOpenEditDialog(true),
          icon: <EditIcon />,
        }}
      />

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6}}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Reseller Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 4}}>
                  <Typography variant="body2" color="text.secondary">Reseller Name</Typography>
                </Grid>
                <Grid size={{ xs: 8}}>
                  <Typography variant="body1">{reseller.name}</Typography>
                </Grid>

                <Grid size={{ xs: 4}}>
                  <Typography variant="body2" color="text.secondary">Contact Details</Typography>
                </Grid>
                <Grid size={{ xs: 8}}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{reseller.contactDetails}</Typography>
                </Grid>

                <Grid size={{ xs: 4}}>
                  <Typography variant="body2" color="text.secondary">Created On</Typography>
                </Grid>
                <Grid size={{ xs: 8}}>
                  <Typography variant="body1">{new Date(reseller.createdAt).toLocaleDateString()}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid size={{ xs: 12, md: 6}}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Customer Summary</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h4">{customers.length}</Typography>
                  <Typography variant="body2">Total Customers</Typography>
                </CardContent>
              </Card>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleAddCustomer}
                  startIcon={<AddIcon />}
                  fullWidth
                >
                  Add New Customer
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="reseller tabs">
          <Tab label="Customers" id="reseller-tab-0" />
          <Tab label="Activity History" id="reseller-tab-1" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <DataTable
          columns={customerColumns}
          data={customers}
          getRowId={(row) => row.customerId}
          onRowClick={(row) => navigate(`/customers/${row.customerId}`)}
          emptyMessage="No customers found for this reseller."
          actions={[
            {
              icon: <ViewIcon />,
              label: 'View Customer',
              onClick: (row) => navigate(`/customers/${row.customerId}`),
            },
          ]}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Alert severity="info">
          Reseller activity tracking will be implemented in a future update.
        </Alert>
      </TabPanel>

      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Reseller</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Reseller Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              margin="normal"
              label="Contact Information"
              value={formData.contactDetails}
              onChange={(e) => setFormData({ ...formData, contactDetails: e.target.value })}
              multiline
              rows={3}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditReseller}
            disabled={!formData.name || !formData.contactDetails}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
